// this data is used to contruct analytics data layer
import { Injectable } from '@angular/core';
import { AnalyticsService } from '@rd/core/services/analytics/analytics.service';
import { EnvService } from '@rd/core/services/env/env.service';
import { Sitemap, TrackData } from '@rd/types';
import { set as setCookie } from 'es-cookie';

export interface VmaSitemap extends Omit<Sitemap, 'homepage'> {
  earn: string;
  redeem: string;
  purchaseEraserHowTo: string;
}

export const sitemap: VmaSitemap = {
  earn: 'earn',
  redeem: 'redeem',
  pointsActivity: 'pointsactivity',
  purchaseEraserHowTo: 'purchaseeraserhowto',
  redeemCash: {
    checkout: 'redeemcash:details',
    review: 'redeemcash:review',
    success: 'redeemcash:confirmation',
  },
  transferConnect: {
    checkout: 'transfer:details',
    review: 'transfer:review',
    success: 'transfer:confirmation',
  },
  giftCard: {
    listing: 'giftcard',
    checkout: 'giftcard:details',
    review: 'giftcard:review',
    success: 'giftcard:confirmation',
    manage: '',
  },
  offers: {
    listing: 'offers',
    details: 'offers:details',
  },
  campaigns: {
    listing: 'campaigns',
    details: 'campaigns:details',
  },
};

@Injectable({
  providedIn: 'root',
})
export class VmaAnalyticsService extends AnalyticsService {
  private developLibUrl =
    'https://assets.adobedtm.com/aaa86c73d744/e3185619c50a/launch-96fa840fcdd2-staging.min.js';
  private uatLibUrl =
    'https://assets.adobedtm.com/aaa86c73d744/e3185619c50a/launch-0da2b6e73f45-development.min.js';
  private productionLibUrl =
    'https://assets.adobedtm.com/aaa86c73d744/e3185619c50a/launch-e70ac855b712.min.js';
  private analyticsUrl = '';
  private pendingTrackingEvent: TrackData[] = [];
  private scriptLoaded = false;
  //
  protected isEnabled = true;
  sitemap = sitemap;

  constructor(private envService: EnvService) {
    super();

    if (envService.isProduction && !envService.isUat) {
      this.analyticsUrl = this.productionLibUrl;
    } else if (envService.isUat) {
      this.analyticsUrl = this.uatLibUrl;
    } else {
      this.analyticsUrl = this.developLibUrl;
    }

    // the analytics script check the sat_track right after it is loaded
    // we need to set this cookie before analytics script is loaded
    // we are contacting VMA for the proper implementation
    setCookie('sat_track', 'true');
  }

  // this adobe script does not handle events are triggered before the analytics script is loaded
  // we need to store them and flush them manually
  private flushPendingTrackingEvents(): void {
    this.scriptLoaded = true;
    // flush the pending events
    this.pendingTrackingEvent.forEach((trackEvent) => {
      this.trackEvent(trackEvent);
    });
    this.pendingTrackingEvent = [];
  }

  protected trackEvent(trackParams: TrackData): void {
    // because VMA doesn't have manage giftcard yet.
    // This guard make sure that if we won't forget implement tracking events for new pages
    if (!trackParams.routeName && !this.envService.isProduction) {
      console.error('routeName is undefined/empty');
      return;
    }
    // track event may trigger before the analytics is loaded
    // push these event to a pending queue and flush them later
    // when the analytics script is loaded
    if (!this.scriptLoaded) {
      this.pendingTrackingEvent.push(trackParams);
      return;
    }

    window.digitalData = {
      page: {
        pageName: `deNovo-loyalty-${trackParams.routeName}`,
        pageURL: window.location.href,
        server: window.location.origin,
      },
    };

    if (window._satellite) {
      // @ts-ignore
      window._satellite.track('page-view');
    }
  }

  protected onAnalyticsScriptLoaded(): void {
    if (window._satellite) {
      // ensure that we don't enable debug mode in production
      if (this.envService.isDevelopment) {
        // enable debug mode if we need view analytics log in console
        // We can also view the log in the chrome extension
        // https://chrome.google.com/webstore/detail/adobe-experience-cloud-de/ocdmogmohccmeicdhlhhgepeaijenapj
        // window._satellite.setDebug(true);
      }
      // we need to call _satellite.pageBottom() when the library load
      // because the first routeChanged event is fired before analytics library is loaded
      window._satellite.pageBottom();
    }
  }

  injectAnalyticsScript(): void {
    if (this.analyticsUrl) {
      // angular remove all the script tag in index.html
      // the only way to inject third party script is via JS
      const node = document.createElement('script');
      node.src = this.analyticsUrl;
      // the script will be fetched in parallel to parsing and evaluated as soon as it is available.
      // https://developer.mozilla.org/en-US/docs/Web/HTML/Element/script
      node.async = true;
      node.addEventListener('load', () => {
        // flush pending track event
        this.onAnalyticsScriptLoaded();
        this.flushPendingTrackingEvents();
      });
      document.head.appendChild(node);
    }
  }
}
